import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { PlanBadge } from '@/components/billing/PlanBadge';
import { LifecycleBadge } from '@/components/admin/LifecycleBadge';
import { ActivationChecklist } from '@/components/admin/ActivationChecklist';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformOrganization } from '@/hooks/usePlatform';
import { normalizePlanTier } from '@/types/billing';
import { formatCurrency, formatDate, formatNumber } from '@/lib/formatters';
import type { PlatformUserItem } from '@/types/platform';

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  );
}

export function AdminOrganizationDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading } = usePlatformOrganization(id);

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (!data) return <p className="text-muted-foreground">Organization not found.</p>;

  const org = data.data;
  const profile = org.profile;

  const userColumns: Column<PlatformUserItem>[] = [
    { key: 'email', header: 'Email', render: (u) => u.email },
    { key: 'name', header: 'Name', render: (u) => u.name },
    { key: 'role', header: 'Role', render: (u) => <span className="capitalize">{u.role.replace('_', ' ')}</span> },
    { key: 'login', header: 'Last login', render: (u) => (u.last_login_at ? formatDate(u.last_login_at) : 'Never') },
  ];

  return (
    <div className="space-y-6">
      <Link
        to="/admin/organizations"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to organizations
      </Link>

      <PageHeader
        eyebrow="Platform Admin"
        title={org.name}
        actions={
          <div className="flex items-center gap-2">
            <PlanBadge tier={normalizePlanTier(org.plan)} />
            <LifecycleBadge stage={org.lifecycle_stage} />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Secured retrievals" value={formatNumber(org.usage.secured_retrievals)} />
        <Stat label="Resources" value={formatNumber(org.usage.resources_created)} />
        <Stat label="Users" value={org.users.length} />
        <Stat label="Connectors" value={org.connectors.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActivationChecklist
          steps={org.onboarding.steps}
          completed={org.onboarding.completed_count}
          total={org.onboarding.total_count}
        />

        <div className="rounded-lg border bg-card p-4 space-y-3">
          <h3 className="text-sm font-semibold">Company profile</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Company size</dt>
            <dd>{profile.company_size || '—'}</dd>
            <dt className="text-muted-foreground">Industry</dt>
            <dd>{profile.industry || '—'}</dd>
            <dt className="text-muted-foreground">Website</dt>
            <dd className="truncate">{profile.website_url || '—'}</dd>
            <dt className="text-muted-foreground">Stack</dt>
            <dd>{profile.primary_stack?.join(', ') || '—'}</dd>
          </dl>
        </div>

        <div className="rounded-lg border bg-card p-4 space-y-2">
          <h3 className="text-sm font-semibold">Billing</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Subscription</dt>
            <dd className="capitalize">
              {(org.subscription?.status as string) || 'none'}
            </dd>
            <dt className="text-muted-foreground">Paid invoices</dt>
            <dd>{org.invoices_summary.paid_count}</dd>
            <dt className="text-muted-foreground">Total paid</dt>
            <dd>{formatCurrency(org.invoices_summary.total_paid_cents)}</dd>
            <dt className="text-muted-foreground">Last payment</dt>
            <dd>
              {org.invoices_summary.last_paid_at
                ? formatDate(org.invoices_summary.last_paid_at)
                : '—'}
            </dd>
          </dl>
        </div>

        <div className="rounded-lg border bg-card p-4 space-y-2">
          <h3 className="text-sm font-semibold">Connectors</h3>
          {org.connectors.length === 0 ? (
            <p className="text-sm text-muted-foreground">None connected.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {org.connectors.map((c) => (
                <li key={c.id} className="flex items-center justify-between">
                  <span className="capitalize">{c.type}</span>
                  <span className="text-muted-foreground capitalize">{c.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Users</h3>
        <DataTable columns={userColumns} data={org.users} keyExtractor={(u) => u.id} />
      </div>
    </div>
  );
}
